import React from 'react';
import stringReplace from 'react-string-replace';

import { withError } from '../../../shared/components/error-boundary';
import Countdown from '../../../shared/components/countdown';
import useSiteConfig from '../../../hooks/useSiteConfig';
import usePageComponents from '../../../hooks/usePageComponents';
import { Helpers } from '../../../core/src/helpers';

import './intra-day-banner.less';

const getCountDown = (activeDeal, dateFormat, hideTimeUnitName) => {
    const { endDate } = activeDeal;
    return <Countdown endtime={endDate} format={dateFormat} hideTimeUnitName={hideTimeUnitName} />;
};

export const getDealsMessagingConfig = (siteConfig, pageConfig) => {
    const {
        defaultMessaging = 'Flash Sale Ending Soon',
        qtyLimitMessage = '{limit} Unit Flash Sale',
        unitMessage = 'Units left at this price',
        dateFormat = 'd?:h?:m?:s',
        hideTimeUnitName = true,
        offerValueThresholdPercent = 10,
    } = siteConfig || {};
    const {
        defaultMessaging: pDefaultMsg,
        qtyLimitMessage: pQtyMsg,
        unitMessage: pUnitMsg,
        dateFormat: pDateFormat,
        hideTimeUnitName: pHideTimeUnitName,
        offerValueThresholdPercent: pOfferValueThresholdPercent,
    } = pageConfig || {};

    return {
        defaultMessaging: pDefaultMsg || defaultMessaging,
        qtyLimitMessage: pQtyMsg || qtyLimitMessage,
        unitMessage: pUnitMsg || unitMessage,
        dateFormat: pDateFormat || dateFormat,
        hideTimeUnitName: typeof pHideTimeUnitName !== 'undefined' ? pHideTimeUnitName : hideTimeUnitName,
        offerValueThresholdPercent:
            typeof pOfferValueThresholdPercent !== 'undefined'
                ? pOfferValueThresholdPercent
                : offerValueThresholdPercent,
    };
};

export const getDynamicMessaging = (
    activeDeal,
    price,
    dateFormat,
    hideTimeUnitName,
    message,
    offerValueThresholdPercent = 0,
) => {
    const { limit, unitsLeft, dealOfferValue, discountPercent: dealOfferDiscountPercent } = activeDeal;

    //match price or percentage discount in message
    message = message.replace(/({\$})|({%})/g, match => {
        if (!price) return '';
        const { discount, discountPercent } = Helpers.getPriceDiscount(price);
        if (match === '{%}') {
            return discount > 0 ? `${Math.floor(discountPercent)}%` : '';
        } else if (match === '{$}') {
            return discount > 0 ? `$${Math.floor(discount)}` : '';
        }
    });

    //match limit
    message = stringReplace(message, new RegExp(/\{limit}/g), (match, i) => {
        if (limit === -1) return '';
        return (
            <React.Fragment key={`redemption-limit-${i}`}>
                <span className="redemption-limit">{limit}</span>
                {match}
            </React.Fragment>
        );
    });

    //match unitsLeft
    message = stringReplace(message, new RegExp(/\{unitsLeft}/g), (match, i) => {
        if (unitsLeft === -1) return '';
        return (
            <React.Fragment key={`units-left-${i}`}>
                <span className="units-left">{unitsLeft}</span>
                {match}
            </React.Fragment>
        );
    });

    //match offerValue with threshold check
    message = stringReplace(message, new RegExp(/\{offerValue}/g), (match, i) => {
        if (dealOfferValue <= 0 || !price?.regularPrice) {
            return '';
        }

        if (dealOfferDiscountPercent < offerValueThresholdPercent) {
            return '';
        }

        const offerValue = Helpers.formatCurrency(dealOfferValue, '$', true, true);
        return (
            <React.Fragment key={`offer-value-${i}`}>
                <span className="offer-value">{` | Save ${offerValue} More!`}</span>
                {match}
            </React.Fragment>
        );
    });

    //match time left
    message = stringReplace(message, new RegExp(/\{timeLeft}/g), (match, i) => {
        return (
            <React.Fragment key={`time-left-${i}`}>
                {getCountDown(activeDeal, dateFormat, hideTimeUnitName)}
                {match}
            </React.Fragment>
        );
    });

    //match new line
    message = stringReplace(message, new RegExp(/\{br}/g), (match, i) => {
        return (
            <React.Fragment key={`new-line-${i}`}>
                <br />
                {match}
            </React.Fragment>
        );
    });

    return message;
};

const getCustomMessage = (defaultMessaging, message) => {
    if (!message) return defaultMessaging;
    if (!/(\{\w+\})/gi.test(message)) {
        return `${message} {timeLeft}`;
    }
    return message;
};

export default withError(({ activeDeal, price, message, children }) => {
    const { intraDayDeal: globalDealsSettings } = useSiteConfig();
    const { intraDayDeals } = usePageComponents(['intraDayDeals']);

    const { defaultMessaging, qtyLimitMessage, dateFormat, hideTimeUnitName, offerValueThresholdPercent } =
        getDealsMessagingConfig(globalDealsSettings, intraDayDeals);

    const hasActiveDeal = !!(activeDeal || price?.deal);
    if (!hasActiveDeal && !children) {
        return null;
    }

    const deal = activeDeal || price?.deal?.activeDeal || {};

    if (message) {
        const customMessage = (
            <span className="deals-countdown">
                {getDynamicMessaging(
                    deal,
                    price,
                    dateFormat,
                    hideTimeUnitName,
                    getCustomMessage(defaultMessaging, message),
                    offerValueThresholdPercent,
                )}
            </span>
        );
        return children
            ? children({ dealCallout: customMessage, hasActiveDeal })
            : hasActiveDeal
              ? customMessage
              : null;
    }

    const dealCallout = (
        <>
            <span className="deals-countdown">
                {getDynamicMessaging(
                    deal,
                    price,
                    dateFormat,
                    hideTimeUnitName,
                    defaultMessaging,
                    offerValueThresholdPercent,
                )}
            </span>
            {qtyLimitMessage && (
                <span className="deals-callout">
                    {getDynamicMessaging(
                        deal,
                        price,
                        dateFormat,
                        hideTimeUnitName,
                        qtyLimitMessage,
                        offerValueThresholdPercent,
                    )}
                </span>
            )}
        </>
    );

    return children ? children({ dealCallout, hasActiveDeal }) : hasActiveDeal ? dealCallout : null;
});
